import { useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapIcon, Loader2, AlertCircle, Search, Navigation,
  ChevronDown, ChevronRight, Clock,
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../lib/api';

// Fix Leaflet default icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const RED_MARKER = L.divIcon({
  className: 'custom-marker',
  html: '<div style="width:28px;height:28px;background:#B70E0C;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="10" r="8" stroke="white" stroke-width="1.5" fill="none"/><path d="M12 4v12M6 10h12" stroke="white" stroke-width="2"/></svg></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const GREY_MARKER = L.divIcon({
  className: 'custom-marker',
  html: '<div style="width:22px;height:22px;background:#9CA3AF;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;"><svg width="10" height="10" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="10" r="8" stroke="white" stroke-width="1.5" fill="none"/><path d="M12 4v12M6 10h12" stroke="white" stroke-width="2"/></svg></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

interface GeoEntry {
  id: string;
  lat: number;
  lng: number;
  fieldName: string;
  timestamp: string;
  rawEntryId: string;
  /** For popup display */
  value: string;
  /** Display label (POI name etc.) */
  label?: string;
}

interface FieldGroup {
  fieldName: string;
  count: number;
  latestLat: number;
  latestLng: number;
  latestTimestamp: string;
  entries: GeoEntry[];
}

const FIELD_COLORS = [
  { bg: '#DBEAFE', text: '#1D4ED8' },   // Blau
  { bg: '#D1FAE5', text: '#047857' },   // Grün
  { bg: '#FED7AA', text: '#C2410C' },   // Orange
  { bg: '#E9D5FF', text: '#6D28D9' },   // Violett
  { bg: '#CCFBF1', text: '#0F766E' },   // Türkis
  { bg: '#FCE7F3', text: '#BE185D' },   // Rosa
  { bg: '#CFFAFE', text: '#0E7490' },   // Cyan
  { bg: '#FEF9C3', text: '#A16207' },   // Gelb
  { bg: '#E0E7FF', text: '#4338CA' },   // Indigo
  { bg: '#FEE2E2', text: '#B91C1C' },   // Hellrot
];

function getFieldColor(index: number) {
  return FIELD_COLORS[index % FIELD_COLORS.length];
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso || '?';
  }
}

function formatDateShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso || '?';
  }
}

export default function MapsPage() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup>(L.layerGroup());
  const [ready, setReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: entriesRaw, isLoading } = useQuery<any[]>({
    queryKey: ['data-entries', 'coords'],
    queryFn: async () => {
      const res = await api.get('/data-fields/entries/all', { params: { limit: 500 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });

  // Parse coordinate entries
  const geoEntries: GeoEntry[] = useMemo(() => {
    const raw = entriesRaw || [];
    const list = Array.isArray(raw) ? raw : (raw as any)?.data || [];
    return list.flatMap((e: any) => {
      const type = e.dataField?.type;
      const val = e.value;
      const fieldName = e.dataField?.name || '?';
      const ts = e.timestamp || e.createdAt || new Date(0).toISOString();
      const rawId = e.id;
      if (!type || !val) return [];

      if (type === 'COORDINATE_PAIR') {
        const parts = val.split(',');
        if (parts.length !== 2) return [];
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (isNaN(lat) || isNaN(lng)) return [];
        return [{
          id: rawId + '-c', lat, lng,
          fieldName, timestamp: ts, rawEntryId: rawId, value: val,
        }];
      }

      if (type === 'GEOJSON') {
        try {
          const geojson = JSON.parse(val);
          const results: GeoEntry[] = [];
          const extractCoords = (coords: number[]) => {
            if (coords.length >= 2) {
              results.push({
                id: rawId + '-g' + results.length,
                lat: coords[1], lng: coords[0],
                fieldName, timestamp: ts,
                rawEntryId: rawId, value: val,
              });
            }
          };
          const walk = (obj: any) => {
            if (!obj || !obj.type) return;
            switch (obj.type) {
              case 'Point': extractCoords(obj.coordinates); break;
              case 'MultiPoint': obj.coordinates?.forEach(extractCoords); break;
              case 'LineString':
              case 'MultiLineString':
              case 'Polygon':
              case 'MultiPolygon': {
                const ring = Array.isArray(obj.coordinates[0]) ? obj.coordinates[0] : obj.coordinates;
                if (ring?.length >= 2) extractCoords(ring[0]);
                break;
              }
              case 'GeometryCollection': obj.geometries?.forEach(walk); break;
              case 'Feature': walk(obj.geometry); break;
              case 'FeatureCollection': obj.features?.forEach(walk); break;
            }
          };
          walk(geojson);
          return results;
        } catch { return []; }
      }

      if (type === 'POI') {
        try {
          const poi = JSON.parse(val);
          if (!poi || typeof poi !== 'object') return [];
          const lat = parseFloat(poi.lat ?? poi.latitude);
          const lng = parseFloat(poi.lon ?? poi.lng ?? poi.longitude);
          if (isNaN(lat) || isNaN(lng)) return [];
          const name = poi.name || poi.title || '';
          return [{
            id: rawId + '-p',
            lat, lng,
            fieldName, timestamp: ts, rawEntryId: rawId,
            value: val,
            label: name,
          }];
        } catch { return []; }
      }

      if (type === 'POI_LIST') {
        try {
          const list = JSON.parse(val);
          if (!Array.isArray(list) || list.length === 0) return [];
          return list
            .map((poi: any, i: number) => {
              const lat = parseFloat(poi.lat ?? poi.latitude);
              const lng = parseFloat(poi.lon ?? poi.lng ?? poi.longitude);
              if (isNaN(lat) || isNaN(lng)) return null;
              const name = poi.name || poi.title || '';
              const entryDesc = poi.description ? ' - ' + poi.description : '';
              return {
                id: rawId + '-pl' + i,
                lat, lng,
                fieldName, timestamp: ts, rawEntryId: rawId,
                value: JSON.stringify(poi),
                label: name + entryDesc,
              } as GeoEntry;
            })
            .filter(Boolean) as GeoEntry[];
        } catch { return []; }
      }

      return [];
    });
  }, [entriesRaw]);

  // Group by fieldName
  const { fieldGroups, latestRawEntryIds } = useMemo(() => {
    const groups = new Map<string, GeoEntry[]>();
    for (const e of geoEntries) {
      const list = groups.get(e.fieldName) || [];
      list.push(e);
      groups.set(e.fieldName, list);
    }

    const latestRawIds = new Set<string>();
    const fieldList: FieldGroup[] = [];

    for (const [fieldName, entries] of groups) {
      entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const latest = entries[0];
      latestRawIds.add(latest.rawEntryId);
      fieldList.push({
        fieldName,
        count: entries.length,
        latestLat: latest.lat,
        latestLng: latest.lng,
        latestTimestamp: latest.timestamp,
        entries,
      });
    }

    fieldList.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
    return { fieldGroups: fieldList, latestRawEntryIds: latestRawIds };
  }, [geoEntries]);

  const filteredFields = useMemo(() => {
    if (!searchQuery) return fieldGroups;
    const q = searchQuery.toLowerCase();
    return fieldGroups.filter(f => f.fieldName.toLowerCase().includes(q));
  }, [fieldGroups, searchQuery]);

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const container = mapRef.current;
    container.style.height = '500px';
    container.style.width = '100%';

    const map = L.map(container, {
      center: [48.2, 16.4],
      zoom: 8,
    });

    L.tileLayer('/tiles/styles/bright/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      maxNativeZoom: 14,
    }).addTo(map);

    markersRef.current.addTo(map);
    mapInstance.current = map;

    const timers: number[] = [];
    const inv = () => map.invalidateSize();
    timers.push(window.setTimeout(inv, 100));
    timers.push(window.setTimeout(inv, 500));
    timers.push(window.setTimeout(inv, 1000));
    timers.push(window.setTimeout(inv, 2000));

    let ro: ResizeObserver | null = null;
    try { ro = new ResizeObserver(() => inv()); ro.observe(container); } catch {}

    setReady(true);

    return () => {
      timers.forEach(clearTimeout);
      if (ro) ro.disconnect();
      map.remove();
      mapInstance.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    if (!ready) return;
    const group = markersRef.current;
    group.clearLayers();

    if (geoEntries.length === 0) return;

    const bounds = L.latLngBounds([]);

    for (const e of geoEntries) {
      const isLatest = latestRawEntryIds.has(e.rawEntryId);
      const icon = isLatest ? RED_MARKER : GREY_MARKER;

      const m = L.marker([e.lat, e.lng], { icon });
      m.bindPopup(`<div style="font-family:sans-serif;font-size:13px;min-width:170px">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${isLatest ? '#B70E0C' : '#9CA3AF'}"></span>
          <b>${e.fieldName}</b>
          ${isLatest
            ? '<span style="font-size:10px;background:#B70E0C;color:white;padding:1px 5px;border-radius:8px">aktuell</span>'
            : '<span style="font-size:10px;background:#E5E7EB;color:#6B7280;padding:1px 5px;border-radius:8px">alt</span>'}
        </div>
        <span style="color:#666">${e.label || e.lat.toFixed(5) + ', ' + e.lng.toFixed(5)}</span><br/>
        <span style="color:#999;font-size:11px">${formatDate(e.timestamp)}</span>
      </div>`);

      group.addLayer(m);
      bounds.extend([e.lat, e.lng]);
    }

    // Full GeoJSON geometries
    const raw = entriesRaw || [];
    const rawList = Array.isArray(raw) ? raw : (raw as any)?.data || [];
    for (const e of rawList) {
      if (e.dataField?.type !== 'GEOJSON' || !e.value) continue;
      try {
        const parsed = JSON.parse(e.value);
        if (!parsed || !parsed.type) continue;
        const isLatest = latestRawEntryIds.has(e.id);
        const gj = L.geoJSON(parsed, {
          pointToLayer: () => L.marker([0, 0], { icon: isLatest ? RED_MARKER : GREY_MARKER }),
          style: {
            color: isLatest ? '#B70E0C' : '#9CA3AF',
            weight: isLatest ? 3 : 2,
            fillOpacity: isLatest ? 0.2 : 0.1,
          },
        });
        const name = e.dataField?.name || 'GeoJSON';
        gj.bindPopup(`<div style="font-family:sans-serif;font-size:13px;min-width:170px">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${isLatest ? '#B70E0C' : '#9CA3AF'}"></span>
            <b>${name}</b>
            ${isLatest
              ? '<span style="font-size:10px;background:#B70E0C;color:white;padding:1px 5px;border-radius:8px">aktuell</span>'
              : '<span style="font-size:10px;background:#E5E7EB;color:#6B7280;padding:1px 5px;border-radius:8px">alt</span>'}
          </div>
          <span style="color:#666">GeoJSON Geometrie</span>
        </div>`);
        group.addLayer(gj);
        if (gj.getBounds().isValid()) bounds.extend(gj.getBounds());
      } catch {}
    }

    if (bounds.isValid()) {
      mapInstance.current?.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [geoEntries, latestRawEntryIds, entriesRaw, ready]);

  const flyTo = (lat: number, lng: number, zoom?: number) => {
    const map = mapInstance.current;
    if (map) {
      map.flyTo([lat, lng], zoom ?? 14, { duration: 1 });
    }
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleExpand = (fieldName: string) => {
    setExpandedField(expandedField === fieldName ? null : fieldName);
  };

  return (
    <>
      <style>{['leaflet-pane img', 'leaflet-tile', 'leaflet-marker-icon', 'leaflet-marker-shadow']
        .map(s => '.' + s + '{max-width:none!important}').join('') +
        '.leaflet-container{width:100%!important;height:100%!important}'}
      </style>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <MapIcon size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kartenansicht</h1>
            <p className="text-sm text-gray-500">
              {geoEntries.length} Einträge · {fieldGroups.length} Felder
              <span className="inline-flex items-center ml-3 gap-1 text-xs">
                <span className="inline-block w-3 h-3 rounded-full" style={{background:'#B70E0C'}}></span> aktuell
                <span className="inline-block w-2.5 h-2.5 rounded-full ml-2" style={{background:'#9CA3AF'}}></span> historisch
              </span>
            </p>
          </div>
        </div>

        {/* Map */}
        <div className="card p-0" style={{ position: 'relative' }}>
          <div ref={mapRef} style={{ height: '500px', width: '100%', minHeight: '500px', position: 'relative' }} />
          {isLoading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: 'rgba(255,255,255,0.8)', zIndex: 1000,
            }}>
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          )}
        </div>

        {/* Field List */}
        <div className="card" ref={listRef}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Felder</h2>
            <span className="text-xs text-gray-400">{filteredFields.length} von {fieldGroups.length}</span>
          </div>

          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Feld suchen..."
              className="input-field pl-10"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* List */}
          {filteredFields.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              {searchQuery ? 'Keine Felder gefunden' : 'Keine Felder mit Koordinaten'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 overflow-y-auto" style={{ maxHeight: '600px' }}>
              {filteredFields.map((f, fieldIdx) => {
                const isExpanded = expandedField === f.fieldName;
                // Timeline entries sorted chronologically (oldest first)
                const timeline = [...f.entries].sort(
                  (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
                );

                return (
                  <div key={f.fieldName}>
                    {/* Field Header */}
                    <button
                      onClick={() => { flyTo(f.latestLat, f.latestLng); setSelectedField(f.fieldName); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0" style={{background: getFieldColor(fieldIdx).bg, color: getFieldColor(fieldIdx).text}}>
                        <Navigation size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{f.fieldName}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded text-[10px] font-medium" style={{background: '#E2E8F0', color: '#475569'}}>{f.count}</span>
                          Eintrag{f.count !== 1 ? 'e' : ''} · zuletzt{' '}
                          {formatDateShort(f.latestTimestamp)}
                        </p>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); toggleExpand(f.fieldName); }}
                        className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                        title="Zeitleiste anzeigen"
                        aria-label="Zeitleiste"
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </button>

                    {/* Timeline */}
                    {isExpanded && (
                      <div className="bg-gray-50 px-3 py-2 space-y-1">
                        {timeline.map((entry, idx) => {
                          const isLatest = idx === timeline.length - 1;
                          return (
                            <button
                              key={entry.id}
                              onClick={() => flyTo(entry.lat, entry.lng, 16)}
                              className="w-full flex items-center gap-3 px-3 py-1.5 rounded text-left hover:bg-white transition-colors text-sm"
                            >
                              {/* Timeline dot + line */}
                              <div className="flex flex-col items-center shrink-0" style={{ width: 16 }}>
                                <div
                                  className={`rounded-full shrink-0 ${isLatest ? 'w-3 h-3' : 'w-2 h-2'}`}
                                  style={{
                                    background: isLatest ? '#B70E0C' : '#9CA3AF',
                                  }}
                                />
                                {idx < timeline.length - 1 && (
                                  <div className="w-px flex-1" style={{ background: '#D1D5DB', minHeight: 16 }} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs truncate ${isLatest ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                                  {entry.label || entry.lat.toFixed(5) + ', ' + entry.lng.toFixed(5)}
                                </p>
                              </div>
                              <span className="text-xs text-gray-400 shrink-0">{formatDateShort(entry.timestamp)}</span>
                              {isLatest && (
                                <span className="text-xs bg-red-700 text-white px-1.5 py-0.5 rounded shrink-0">aktuell</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
